import { useMutation } from "@tanstack/react-query";
import { Camera, Loader2, Search, X } from "lucide-react";
import React, { Suspense, useState } from "react";

import { findInOrder, type FindResponse } from "@/api/order_journal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDateTime } from "@/lib/format";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { LEGACY_TEXT, fmtQty, lineTitle, palletNumberText, palletTitle } from "@/lib/order-journal";
import { GOODS_AND_QR_FORMATS } from "@/lib/scan-formats";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { apiErrorText } from "@/lib/scan-ui/screen";

/**
 * Поиск по коду внутри заказа: короб, код маркировки, штрихкод, GTIN, SSCC →
 * паллеты, где код отсканирован. Поле принимает и ручной ввод, и сканер-
 * клавиатуру (Enter в конце); камера — общий `CameraScanner`.
 */
export function CodeSearch({
  orderRef,
  onPickPallet,
}: {
  orderRef: string;
  onPickPallet: (palletId: string) => void;
}): React.ReactElement {
  usePreloadChunk(CameraScanner);
  const [code, setCode] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  useBackGuard(cameraOpen, () => setCameraOpen(false));

  const findM = useMutation({
    mutationFn: (c: string) => findInOrder(orderRef, c),
  });

  const run = (raw: string) => {
    const c = raw.trim();
    if (!c) return;
    setCode(c);
    findM.mutate(c);
  };

  return (
    <div className="space-y-2">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          run(code);
        }}
      >
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Код для поиска</span>
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Код короба, паллеты, маркировки или штрихкод"
            className="pl-8 font-mono"
            aria-label="Код для поиска"
            autoComplete="off"
          />
        </label>
        <Button type="submit" disabled={!code.trim() || findM.isPending}>
          {findM.isPending && <Loader2 className="size-4 animate-spin" />}
          Найти
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Сканировать камерой"
          onClick={() => setCameraOpen(true)}
        >
          <Camera className="size-4" />
        </Button>
      </form>

      {findM.isError && (
        <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
          Поиск не выполнен: {apiErrorText(findM.error)}
        </div>
      )}
      {findM.data && (
        <FindResults data={findM.data} onPick={onPickPallet} onClear={() => findM.reset()} />
      )}

      {cameraOpen && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-[70] bg-black">
            <CameraScanner
              formats={GOODS_AND_QR_FORMATS}
              onDetect={(raw) => {
                setCameraOpen(false);
                run(raw);
              }}
              onClose={() => setCameraOpen(false)}
            />
          </div>
        </Suspense>
      )}
    </div>
  );
}

function FindResults({
  data,
  onPick,
  onClear,
}: {
  data: FindResponse;
  onPick: (palletId: string) => void;
  onClear: () => void;
}): React.ReactElement {
  const pallets = new Set(data.hits.map((h) => h.pallet_id));
  return (
    <div className="rounded-md border bg-background" data-testid="find-results">
      <div className="flex items-center gap-2 border-b px-2 py-1.5 text-sm">
        <span className="min-w-0 flex-1 break-all">
          {data.hits.length === 0 ? (
            <>
              Код <span className="font-mono">{data.code}</span> в сканах этого заказа не найден
            </>
          ) : (
            <>
              Код <span className="font-mono">{data.code}</span>: совпадений {data.hits.length}, паллет{" "}
              {pallets.size}
              {data.truncated && " (показаны первые совпадения — уточните код)"}
            </>
          )}
        </span>
        <button
          type="button"
          onClick={onClear}
          className="flex size-7 shrink-0 items-center justify-center text-muted-foreground"
          aria-label="Закрыть результаты поиска"
        >
          <X className="size-4" />
        </button>
      </div>
      {data.hits.length > 0 && (
        <div className="max-h-72 divide-y overflow-y-auto">
          {data.hits.map((h) => (
            <button
              key={h.item_id}
              type="button"
              onClick={() => onPick(h.pallet_id)}
              className="block w-full px-2 py-1.5 text-left text-sm hover:bg-accent"
            >
              <span className="font-medium">
                {palletTitle(
                  palletNumberText({
                    cargo_unit_id: h.pallet_cargo_unit_id,
                    sscc: h.pallet_sscc,
                    archive_number: h.pallet_archive_number,
                  }),
                  h.pallet_label,
                )}
              </span>
              {h.session_display_number !== null && (
                <span className="text-xs text-muted-foreground"> · проверка № {h.session_display_number}</span>
              )}
              <span className="block break-words text-xs text-muted-foreground">
                {lineTitle({
                  nomenclature_name: h.nomenclature_name ?? "",
                  characteristic_name: h.characteristic_name ?? "",
                  partia_name: h.partia_name ?? "",
                })}{" "}
                · {fmtQty(h.qty_base)} · {fmtDateTime(h.scanned_at)} · {h.scanned_by ?? LEGACY_TEXT}
              </span>
              <span className="block break-all font-mono text-[11px] text-muted-foreground">{h.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
